import React, { useEffect, useState } from "react";
import { voteForQuote } from "./api";
import { RoryQuote } from "./types";

const STORAGE_KEY = "rory-voted-quotes";

const readVoted = (): number[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
};

const markVoted = (id: number) => {
  try {
    const voted = readVoted();
    if (!voted.includes(id)) localStorage.setItem(STORAGE_KEY, JSON.stringify([...voted, id]));
  } catch {
    // Nothing to do; the vote still counts on the server.
  }
};

const HeartIcon = ({ filled }: { filled: boolean }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round">
    <path d="M12 21s-6.7-4.4-9.3-8.6C.6 9 2 4.5 6.2 4.5c2.2 0 3.6 1.2 4.4 2.4h2.8c.8-1.2 2.2-2.4 4.4-2.4 4.2 0 5.6 4.5 3.5 7.9C18.7 16.6 12 21 12 21z" />
  </svg>
);

// One heart per quote per browser. The count is bumped straight away and
// then settled from the server's answer.
interface Props {
  quote: RoryQuote;
  onVoted?: (id: number, votes: number) => void;
}

const UpvoteButton = ({ quote, onVoted }: Props) => {
  const [votes, setVotes] = useState(quote.votes);
  const [voted, setVoted] = useState(() => readVoted().includes(quote.id));
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setVotes(quote.votes);
    setVoted(readVoted().includes(quote.id));
  }, [quote.id, quote.votes]);

  const vote = async () => {
    if (voted || pending) return;
    setPending(true);
    setVoted(true);
    setVotes((v) => v + 1);
    try {
      const total = await voteForQuote(quote.id);
      markVoted(quote.id);
      setVotes(total);
      if (onVoted) onVoted(quote.id, total);
    } catch {
      // Not counted: put things back so they can try again.
      setVoted(false);
      setVotes(quote.votes);
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      className={`rq-heart ${voted ? "is-voted" : ""}`}
      onClick={vote}
      disabled={voted || pending}
      aria-pressed={voted}
      aria-label={voted ? `You liked this quote. ${votes} votes so far.` : `Upvote this quote. ${votes} votes so far.`}
    >
      <HeartIcon filled={voted} />
      <span>{votes}</span>
    </button>
  );
};

export default UpvoteButton;
